import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown } from "lucide-react";
import { useIsPremium } from "@/components/PremiumGate";
import { api } from "@/lib/api";
import { AlertTriangle, Info, Lightbulb, Sparkles } from "lucide-react";

const SEV = {
  warning: { icon: AlertTriangle, color: "#D94F3D", label: "Warning" },
  info: { icon: Info, color: "#A67C00", label: "Note" },
  tip: { icon: Lightbulb, color: "#2E6F40", label: "Tip" },
};

function InsightCard({ item, testid }) {
  const sev = SEV[item.severity] || SEV.info;
  const Icon = sev.icon;
  return (
    <div className="border border-[#D8D4C7] bg-white p-5 relative" data-testid={testid}>
      <div className="absolute top-0 left-0 bottom-0 w-[3px]" style={{ background: sev.color }} />
      <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase" style={{ color: sev.color }}>
        <Icon className="w-3.5 h-3.5" /> {sev.label}{item.category ? ` · ${item.category}` : ""}
      </div>
      <h4 className="font-serif text-xl tracking-tight mt-2">{item.title}</h4>
      <p className="text-sm text-[#3C4537] mt-1 leading-relaxed">{item.detail}</p>
    </div>
  );
}

function PremiumUpsell({ heading, testid }) {
  return (
    <section data-testid={testid}>
      <div className="border border-[#A67C00] bg-[#FDFBF7] p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="insights-premium-upsell">
        <div className="flex items-center gap-3">
          <Crown className="w-5 h-5 text-[#A67C00]" />
          <div>
            <div className="font-serif text-xl tracking-tight">{heading} <span className="text-[#A67C00]">— a Premium feature.</span></div>
            <div className="text-xs text-[#6C7565] mt-0.5">AI reads your ledger and flags overspends, trends and anomalies automatically.</div>
          </div>
        </div>
        <Link to="/pricing" data-testid="insights-upsell-cta" className="bg-[#A67C00] text-white px-4 py-2.5 text-[10px] uppercase tracking-[0.25em] hover:bg-[#1A1F16] transition-colors shrink-0">
          Unlock with Premium
        </Link>
      </div>
    </section>
  );
}

function InsightsSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {[0, 1, 2].map((i) => (
        <div key={i} className="border border-[#D8D4C7] bg-white p-5 animate-pulse">
          <div className="h-2.5 w-20 bg-[#EAE7DD]" />
          <div className="h-5 w-3/4 bg-[#EAE7DD] mt-3" />
          <div className="h-3 w-full bg-[#EAE7DD] mt-2" />
        </div>
      ))}
    </div>
  );
}

export function SpendingInsights({ variant = "cards", kicker, heading, testid }) {
  const isPremium = useIsPremium();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isPremium) { setLoading(false); return; }
    api.get("/ai/spending-insights")
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isPremium]);

  if (!isPremium) return <PremiumUpsell heading={heading} testid={testid} />;

  const items = data ? (variant === "cards" ? data.cards : data.category_insights) : [];
  if (!loading && items.length === 0) return null;

  return (
    <section data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-3.5 h-3.5 text-[#A67C00]" />
        <div>
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#A67C00]">{kicker}</span>
          <h3 className="font-serif text-2xl tracking-tight">{heading}</h3>
        </div>
      </div>
      {loading ? (
        <InsightsSkeleton />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item, i) => (
            <InsightCard key={`${i}-${item.severity}-${item.title}`} item={item} testid={`${testid}-card-${i}`} />
          ))}
        </div>
      )}
    </section>
  );
}
